import { useMemo, useRef, useState } from "react";
import { Gauge, LocateFixed, Power, SlidersHorizontal } from "lucide-react";
import type { GestureInput } from "../audio/types";
import type { Instrument } from "../data/instruments";
import { GyroInputManager, type MotionPermissionState } from "./GyroInputManager";

interface MobilePerformanceToolbarProps {
  instrument: Instrument;
  onControl: (controlId: string, value?: number | string | boolean) => void;
  onNoteStart: (gesture: GestureInput) => void;
  onNoteEnd: (noteName: string) => void;
}

function mapMotionFrame(
  instrumentId: string,
  frame: { pitch: number; roll: number; shake: number },
  onControl: MobilePerformanceToolbarProps["onControl"]
) {
  const pitch = (frame.pitch + 1) / 2;
  const roll = (frame.roll + 1) / 2;

  if (instrumentId === "gayageum") {
    onControl("nonghyeon_depth", roll);
  } else if (instrumentId === "oud") {
    onControl("micro_bend_amount", roll);
  } else if (instrumentId === "sitar") {
    onControl("sitar_meend_mode", roll > 0.72 ? "expert" : roll > 0.36 ? "standard" : "beginner");
  } else if (instrumentId === "daegeum" || instrumentId === "ney") {
    onControl("breath_pressure", pitch);
  } else if (instrumentId === "yidaki_didgeridoo") {
    onControl("breath_pressure", pitch);
    onControl("mouth_shape_y", roll);
  } else if (instrumentId === "gamelan_saron") {
    onControl("ombak_depth", roll);
  } else if (instrumentId === "mbira" || instrumentId === "kora") {
    onControl("buzz_amount", roll);
  } else if (instrumentId === "tabla") {
    onControl("bayan_pressure", pitch);
  }
}

export function MobilePerformanceToolbar({
  instrument,
  onControl,
  onNoteStart,
  onNoteEnd
}: MobilePerformanceToolbarProps) {
  const manager = useMemo(() => new GyroInputManager(), []);
  const [status, setStatus] = useState<MotionPermissionState>(() => manager.getStatus());
  const [sensitivity, setSensitivity] = useState(0.5);
  const lastShakeAt = useRef(0);

  const triggerShakeAccent = (shake: number) => {
    const now = performance.now();

    if (shake < 0.68 || now - lastShakeAt.current < 420) {
      return;
    }

    lastShakeAt.current = now;
    const noteName = instrument.playableNotes[0] ?? "C4";
    onNoteStart({ source: "pointer", noteName, velocity: 0.92, ornament: "motion_accent" });
    window.setTimeout(() => onNoteEnd(noteName), 180);
  };

  const enableMotion = async () => {
    const nextStatus = await manager.enable((frame) => {
      mapMotionFrame(instrument.id, frame, onControl);
      triggerShakeAccent(frame.shake);
    });

    setStatus(nextStatus);
  };

  return (
    <section className="mobile-performance-toolbar" data-testid="mobile-performance-toolbar" aria-label="Mobile performance controls">
      <div className="motion-status-row">
        <button type="button" aria-label="Enable Motion Control" data-testid="motion-enable-button" onClick={() => void enableMotion()}>
          <Gauge size={17} aria-hidden="true" />
          <span>Enable Motion Control</span>
        </button>
        <span className="motion-status" data-testid="motion-status">
          {status}
        </span>
      </div>
      <div className="motion-controls">
        <button
          type="button"
          aria-label="Calibrate motion controls"
          onClick={() => {
            manager.calibrate();
            setStatus(status === "unsupported" ? "fallback_touch" : status);
          }}
        >
          <LocateFixed size={17} aria-hidden="true" />
          <span>Calibrate</span>
        </button>
        <button
          type="button"
          aria-label="Turn motion controls off"
          onClick={() => {
            manager.disable();
            setStatus(manager.getStatus() === "unsupported" ? "fallback_touch" : "needs_permission");
          }}
        >
          <Power size={17} aria-hidden="true" />
          <span>Motion off</span>
        </button>
        <label className="surface-slider motion-sensitivity">
          <span>
            <SlidersHorizontal size={15} aria-hidden="true" />
            Sensitivity
          </span>
          <input
            aria-label="Motion sensitivity"
            data-testid="motion-sensitivity-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={sensitivity}
            onChange={(event) => {
              const value = Number(event.currentTarget.value);
              setSensitivity(value);
              manager.setSensitivity(value);
            }}
          />
        </label>
      </div>
      <p className="mobile-fallback-note">
        {status === "granted"
          ? "Tilt controls are smoothed and calibrated for this instrument."
          : "Touch sliders remain available when motion control is unavailable or denied."}
      </p>
    </section>
  );
}
